import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Loader2,
  RefreshCw,
  UploadCloud,
  type LucideIcon,
} from "lucide-react";
import { Fragment, useId, useState } from "react";
import { toast } from "sonner";
import { api, type SyncRun, type SyncRunTrigger } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { downloadBlob } from "@/lib/download-blob";
import { formatDuration, formatTimestamp } from "@/lib/job-format";

const POLL_INTERVAL_MS = 5_000;

// A page of runs: a night is one row, so a month of nights fits in three pages.
const DEFAULT_PAGE_SIZE = 10;

// Columns, so the expanded detail row can span all of them.
const COLUMN_COUNT = 5;

const NUMBER = new Intl.NumberFormat("en-GB");

// What asked for the run, in the panel's words rather than the API's.
const TRIGGER_LABELS: Record<SyncRunTrigger, string> = {
  nightly: "Nightly run",
  changed: "Queue changes",
  backlog: "Catalogue backlog",
  product: "One product",
};

interface StateStyle {
  label: string;
  icon: LucideIcon;
  variant: "default" | "secondary" | "destructive" | "outline";
  spin?: boolean;
}

// Icon + text on every badge, never colour alone (accessibility rule).
const RUNNING: StateStyle = {
  label: "Running",
  icon: Loader2,
  variant: "secondary",
  spin: true,
};
const DONE: StateStyle = { label: "Done", icon: CheckCircle2, variant: "default" };
const WITH_ERRORS: StateStyle = {
  label: "With errors",
  icon: AlertTriangle,
  variant: "destructive",
};

export interface SyncRunsCardProps {
  /** Runs per page. */
  pageSize?: number;
}

export function SyncRunsCard({ pageSize = DEFAULT_PAGE_SIZE }: SyncRunsCardProps) {
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["sync-runs", pageSize, offset],
    queryFn: () => api.syncRuns(pageSize, offset),
    // The nightly run opens a run of its own, so there is no local event to
    // invalidate this on — it has to keep pulling.
    refetchInterval: POLL_INTERVAL_MS,
    placeholderData: keepPreviousData,
  });

  const syncChanged = useMutation({
    mutationFn: () => api.syncChanged(),
    onSuccess: (result) => {
      // Products without a description are the common reason for a small
      // batch, so say it out loud rather than looking like a broken trigger.
      const heldBack =
        result.skippedWithoutDescription > 0
          ? `${result.skippedWithoutDescription} without description, not publishable yet`
          : null;

      if (result.enqueued === 0) {
        toast.info(heldBack ? `Nothing to sync: ${heldBack}` : "No products pending sync");
      } else {
        const notes = [
          result.truncated ? "more remain outside this batch" : null,
          heldBack,
        ].filter(Boolean);
        toast.success(
          `${result.enqueued} products queued for Shopify` +
            (notes.length > 0 ? ` (${notes.join("; ")})` : ""),
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["sync-runs"] });
    },
    onError: (mutationError) => {
      toast.error(
        mutationError instanceof Error
          ? `Could not queue the sync: ${mutationError.message}`
          : "Could not queue the sync",
      );
    },
  });

  const runs = data?.runs ?? [];
  const total = data?.total ?? 0;
  const first = total === 0 ? 0 : offset + 1;
  const last = offset + runs.length;

  return (
    <Card>
      <CardHeader className="space-y-4">
        <div className="flex flex-row items-start justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle>Shopify sync</CardTitle>
            <CardDescription>
              One row per run — a night, or a press of a button here. Open a run for what
              it wrote, and download its CSV for every condition it touched. Kept 30 days.
            </CardDescription>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={isFetching ? "size-4 animate-spin" : "size-4"} />
              Refresh
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => syncChanged.mutate()}
              disabled={syncChanged.isPending}
            >
              <UploadCloud className="size-4" />
              {syncChanged.isPending ? "Queuing..." : "Queue changes"}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : isError ? (
          <p className="text-sm text-destructive">
            Could not load the sync runs
            {error instanceof Error ? `: ${error.message}` : "."}
          </p>
        ) : runs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No syncs in the last 30 days.</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground" role="status">
              {`${NUMBER.format(first)}–${NUMBER.format(last)} of ${NUMBER.format(total)} runs`}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full caption-bottom text-sm">
                <caption className="sr-only">Shopify sync runs, newest first</caption>
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th scope="col" className="pb-2 pr-4 font-medium">
                      Run
                    </th>
                    <th scope="col" className="pb-2 pr-4 font-medium">
                      Status
                    </th>
                    <th scope="col" className="pb-2 pr-4 font-medium">
                      Started
                    </th>
                    <th scope="col" className="pb-2 pr-4 font-medium">
                      Duration
                    </th>
                    <th scope="col" className="pb-2 font-medium">
                      Report
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <RunRow key={run.id} run={run} />
                  ))}
                </tbody>
              </table>
            </div>
            {total > pageSize && (
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - pageSize))}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={last >= total}
                  onClick={() => setOffset(offset + pageSize)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function RunRow({ run }: { run: SyncRun }) {
  const [expanded, setExpanded] = useState(false);
  const detailId = useId();
  const state = stateOf(run);
  const StateIcon = state.icon;

  // Ignores the click that ends a text selection.
  const toggle = () => {
    if (window.getSelection()?.isCollapsed === false) return;
    setExpanded((open) => !open);
  };

  return (
    <>
      <tr
        className="cursor-pointer border-b border-border align-top last:border-0 hover:bg-muted/50"
        onClick={() => toggle()}
      >
        <td className="py-2 pr-4 font-medium">
          {/* The row toggles on click; this button is the keyboard and
              screen-reader control, styled as the trigger name. */}
          <button
            type="button"
            className="rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-expanded={expanded}
            aria-controls={detailId}
            onClick={(event) => {
              event.stopPropagation();
              toggle();
            }}
          >
            {TRIGGER_LABELS[run.trigger]}
            <span className="sr-only"> ({expanded ? "hide" : "show"} the details)</span>
          </button>
        </td>
        <td className="py-2 pr-4">
          <Badge variant={state.variant} className="gap-1">
            <StateIcon
              aria-hidden="true"
              className={state.spin ? "size-3.5 animate-spin" : "size-3.5"}
            />
            {state.label}
          </Badge>
          {run.finishedAt === null && (
            <span className="ml-2 text-xs text-muted-foreground tabular-nums">
              {`${NUMBER.format(run.done)} of ${NUMBER.format(run.enqueued)}`}
            </span>
          )}
        </td>
        <td className="py-2 pr-4 whitespace-nowrap tabular-nums">
          {formatTimestamp(run.startedAt)}
        </td>
        <td className="py-2 pr-4 whitespace-nowrap tabular-nums">
          {formatDuration(run.startedAt, run.finishedAt)}
        </td>
        <td className="py-2">
          <ReportButton run={run} />
        </td>
      </tr>
      {expanded && (
        <tr data-run-detail className="border-b border-border last:border-0">
          <td colSpan={COLUMN_COUNT} className="pb-3">
            <div id={detailId} className="space-y-3">
              <div className="rounded-md bg-muted p-3 text-xs leading-relaxed">
                {/* Two label/value pairs per line where there is width, one
                    otherwise; values aligned in a column. */}
                <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 sm:grid-cols-[1fr_auto_1fr_auto]">
                  {counters(run).map(([label, value]) => (
                    <Fragment key={label}>
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd className="text-right font-mono tabular-nums text-foreground">
                        {NUMBER.format(value)}
                      </dd>
                    </Fragment>
                  ))}
                </dl>
                {notes(run).length > 0 && (
                  <div className="mt-2 space-y-1">
                    {notes(run).map((note) => (
                      <p
                        key={note.text}
                        className={note.alarming ? "text-destructive" : "text-muted-foreground"}
                      >
                        {note.text}
                      </p>
                    ))}
                  </div>
                )}
              </div>
              <p className="font-mono text-xs break-all text-muted-foreground">{run.id}</p>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ReportButton({ run }: { run: SyncRun }) {
  const download = useMutation({
    mutationFn: () => api.syncRunReport(run.id),
    onSuccess: (blob) => downloadBlob(blob, reportFileName(run)),
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? `Could not download the report: ${error.message}`
          : "Could not download the report",
      );
    },
  });

  // A run still going has no CSV yet, and one that wrote nothing never will.
  if (run.reportKey === null) return <span className="text-muted-foreground">—</span>;

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="gap-1.5 whitespace-nowrap"
      disabled={download.isPending}
      onClick={(event) => {
        event.stopPropagation();
        download.mutate();
      }}
    >
      {download.isPending ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <Download className="size-3.5" />
      )}
      {/* Kept out of the accessible name, which the sr-only text gives. */}
      <span aria-hidden="true">CSV</span>
      <span className="sr-only">
        Download what the {TRIGGER_LABELS[run.trigger].toLowerCase()} of{" "}
        {formatTimestamp(run.startedAt)} wrote
      </span>
    </Button>
  );
}

// Trigger and date rather than the run id, which carries a timestamp to the
// millisecond.
function reportFileName(run: SyncRun): string {
  const stamp = new Date(run.startedAt).toISOString().slice(0, 10);
  return `shopify-sync-${run.trigger}-${stamp}.csv`;
}

function stateOf(run: SyncRun): StateStyle {
  if (run.finishedAt === null) return RUNNING;
  return run.counters.productsFailed > 0 ? WITH_ERRORS : DONE;
}

// Same counters in the same order every time, zeros included, so a run also
// shows what it did not do.
function counters(run: SyncRun): ReadonlyArray<readonly [string, number]> {
  return [
    ["Products sent", run.enqueued],
    ["Products created", run.counters.productsCreated],
    ["Products updated", run.counters.productsUpdated],
    ["Products failed", run.counters.productsFailed],
    ["Conditions added", run.counters.variantsCreated],
    ["Conditions updated", run.counters.variantsUpdated],
    ["Conditions zeroed", run.counters.variantsZeroed],
    ["Prices changed", run.counters.pricesChanged],
    ["Stock changed", run.counters.stockChanged],
  ];
}

// Why the counters mean less than they look.
function notes(run: SyncRun): ReadonlyArray<{ text: string; alarming?: boolean }> {
  const notes: Array<{ text: string; alarming?: boolean }> = [];
  if (run.stoppedReason) {
    notes.push({ text: `Stopped early: ${run.stoppedReason}`, alarming: true });
  }
  if (run.heldBackWithoutDescription > 0) {
    notes.push({
      text: `${NUMBER.format(run.heldBackWithoutDescription)} product(s) held back: no description yet, so they cannot be published`,
    });
  }
  if (run.finishedAt !== null && run.reportKey === null && run.done > 0) {
    notes.push({ text: "No CSV was stored for this run", alarming: true });
  }
  return notes;
}
